import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Play, Square } from 'lucide-react';
import {
  ApiError,
  getExperimentResults,
  getGoals,
  qk,
  setExperimentStatus,
  type Experiment,
} from '../api.js';
import BilgiDugmesi from '../components/BilgiDugmesi.js';
import Badge from '../components/Badge.js';
import Card from '../components/Card.js';
import EmptyState, { ErrorState } from '../components/EmptyState.js';
import { LoadingBlock } from '../components/Spinner.js';
import { DurumMetni, ResultsTable, STATUS_META } from '../components/DeneySonuc.js';
import DeneyTarihleri from '../components/DeneyTarihleri.js';
import DeneyUygulamaDurumu from '../components/DeneyUygulamaDurumu.js';

/**
 * §102.2 — DENEY DETAY SAYFASI (`/deneyler/:id`).
 *
 * Deneyin kendi adresi yoktu: paylaşmak, yer imlemek, e-postadan doğrudan açmak
 * mümkün değildi. Bu sayfa onu verir ve §102.3'ün tarih sözleşmesini tam hâliyle
 * çizer (kartta tek satır, burada ızgara).
 *
 * VERİ KAYNAĞI: mevcut `GET /experiments/:id/results` — `{experiment, results}`
 * döndürdüğü için tek sorgu hem başlığı hem tabloyu besler. `GET /experiments/:id`
 * ucu BİLEREK yazılmadı (§101.2 KARAR 2): aynı kaydın ikinci okuma yolu olurdu ve
 * `dead-surface-check` hangisi kullanılmadan kalırsa onu bulgu yazardı.
 *
 * DEĞİŞMEZ KURAL burada da geçerlidir: motor `concluded` demeden "kazanan/önde"
 * kelimesi GEÇMEZ. Detay daha çok yer sunar; kural gevşemez.
 */

/** §61.2 KARAR 3 — deney hedefi: NULL ise projenin aktif hedefi ölçülür. */
function HedefSatiri({ experiment }: { experiment: Experiment }) {
  const goals = useQuery({
    queryKey: qk.goals(experiment.pid),
    queryFn: ({ signal }) => getGoals(experiment.pid, signal),
    // Hedef adı bir SÜS değil ama sayfanın omurgası da değil: hata hâlinde
    // altındaki her şey çizilmeye devam etsin.
    retry: false,
    enabled: experiment.goalId !== null,
  });

  if (experiment.goalId === null) {
    return (
      <>
        <dt className="text-[12px] text-[#9ca3af]">Hedef</dt>
        <dd className="text-[13px] font-medium text-[#111827]">Aktif hedef (otomatik)</dd>
      </>
    );
  }

  const goal = (goals.data ?? []).find((g) => g.id === experiment.goalId);
  return (
    <>
      <dt className="text-[12px] text-[#9ca3af]">Hedef</dt>
      <dd className="text-[13px] font-medium text-[#111827]">
        {goal === undefined ? `#${experiment.goalId}` : goal.url_pattern}
      </dd>
    </>
  );
}

export default function DeneyDetay() {
  const { id = '' } = useParams<{ id: string }>();
  const client = useQueryClient();

  const sorgu = useQuery({
    queryKey: qk.experimentResults(id),
    queryFn: ({ signal }) => getExperimentResults(id, signal),
  });

  const durum = useMutation({
    mutationFn: (status: 'running' | 'stopped') => setExperimentStatus(id, status),
    onSuccess: (guncel) => {
      void client.invalidateQueries({ queryKey: qk.experimentResults(id) });
      void client.invalidateQueries({ queryKey: qk.experiments(guncel.pid) });
    },
  });

  const geriBaglantisi = (
    <Link
      to="/deneyler"
      data-testid="deney-detay-geri"
      className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#374151]"
    >
      <ArrowLeft size={14} /> Deneyler
    </Link>
  );

  /*
   * §27.1 — erişilemeyen kayıt da "yok" görünür (enumeration koruması). Panel bu
   * ayrımı YAPMAZ; ham hata metni basmak yerine listeye dönüş yolu verir.
   */
  const bulunamadi = sorgu.isError && sorgu.error instanceof ApiError && sorgu.error.status === 404;

  return (
    <div className="min-h-full bg-[#fafafa]" data-testid="deney-detay-sayfasi">
      <div className="border-b border-[#e5e7eb] bg-white px-6 py-4">
        {geriBaglantisi}
        {sorgu.data !== undefined ? (
          <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-[18px] font-semibold text-[#111827]">
                  {sorgu.data.experiment.name}
                </h1>
                <Badge tone={STATUS_META[sorgu.data.experiment.status].tone}>
                  {STATUS_META[sorgu.data.experiment.status].label}
                </Badge>
                <BilgiDugmesi testId="deney-detay-bilgi">
                  Deneyin tam kaydı: tarihler, varyant ağırlıkları ve kol sonuçları. "Kazanan"
                  ilanını panel değil istatistik motoru verir (§64) — sürerken yalnız kol sayıları
                  ve always-valid aralık gösterilir. Otomatik kapanış tarihi motorun uyguladığı
                  sınırdan okunur, panelde hesaplanmaz (§102.3).
                </BilgiDugmesi>
              </div>
              {sorgu.data.experiment.hypothesis !== '' ? (
                <p className="mt-1 text-[13px] text-[#6b7280]">{sorgu.data.experiment.hypothesis}</p>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              {sorgu.data.experiment.status === 'draft' ? (
                <button
                  type="button"
                  data-testid="deney-detay-baslat"
                  onClick={() => durum.mutate('running')}
                  disabled={durum.isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-50"
                >
                  <Play size={14} /> Başlat
                </button>
              ) : null}
              {sorgu.data.experiment.status === 'running' ? (
                <button
                  type="button"
                  data-testid="deney-detay-durdur"
                  onClick={() => durum.mutate('stopped')}
                  disabled={durum.isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[#e5e7eb] px-3 py-1.5 text-[13px] font-medium text-[#374151] disabled:opacity-50"
                >
                  <Square size={14} /> Durdur
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <div className="space-y-6 px-6 py-8">
        {sorgu.isLoading ? <LoadingBlock /> : null}

        {bulunamadi ? (
          <div data-testid="deney-detay-bulunamadi">
            <EmptyState
              icon="?"
              title="Deney bulunamadı"
              description="Bu adres bir deneye karşılık gelmiyor — silinmiş ya da başka bir projeye ait olabilir."
            />
          </div>
        ) : null}

        {sorgu.isError && !bulunamadi ? (
          <ErrorState
            title="Deney okunamadı"
            description="Sunucuya ulaşılamıyor."
            onRetry={() => void sorgu.refetch()}
          />
        ) : null}

        {sorgu.data !== undefined ? (
          <>
            {/* §102.3 — tarih sözleşmesi: gerçek olan yazılır, olmayan söylenir. */}
            <Card title="Zaman çizelgesi">
              <DeneyTarihleri experiment={sorgu.data.experiment} />
            </Card>

            {/* §107 — deney NEYİ test ediyor ve gerçekten uygulanıyor mu. */}
            <Card title="Kurulum" subtitle="Running deneyde varyant ve hedef DEĞİŞTİRİLEMEZ (§62.1).">
              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
                <div>
                  <dt className="text-[12px] text-[#9ca3af]">Test yüzeyi</dt>
                  <dd
                    className="text-[13px] font-medium text-[#111827]"
                    data-testid="deney-detay-yuzey"
                  >
                    {/*
                      §107.3 KARAR 1 + §109.4 — yüzey İKİ biçimde olabilir: inline
                      deneyde desen, split-URL deneyinde varyant SAYFALARI (orada
                      `targetPattern` tanım gereği NULL'dır). İkincisinde "tanımsız"
                      yazmak, doğru kurulmuş bir deneyi eksik göstermek olurdu.
                    */}
                    {sorgu.data.experiment.targetPattern ??
                      (sorgu.data.experiment.variants.some((v) => v.url !== undefined)
                        ? 'varyant sayfaları (split-URL)'
                        : 'tanımsız')}
                  </dd>
                </div>
                <div>
                  <HedefSatiri experiment={sorgu.data.experiment} />
                </div>
                <div>
                  <dt className="text-[12px] text-[#9ca3af]">Deney kimliği</dt>
                  <dd className="num text-[13px] font-medium text-[#111827]">
                    {sorgu.data.experiment.id}
                  </dd>
                </div>
              </dl>

              {/* §107.3 KARAR 2 — her varyantın NE GÖSTERDİĞİ. */}
              <dl className="mt-4 space-y-1.5" data-testid="deney-detay-varyantlar">
                <dt className="text-[12px] text-[#9ca3af]">Varyantlar</dt>
                {sorgu.data.experiment.variants.map((v) => (
                  <dd key={v.key} className="text-[13px] text-[#111827]">
                    <span className="font-medium">{v.key}</span>{' '}
                    <span className="text-[#6b7280]">%{Math.round(v.weight * 100)}</span>
                    {v.url === undefined ? null : (
                      <span className="num ml-2 text-[12px] text-[#374151]">{v.url}</span>
                    )}
                    {v.label === undefined || v.label === '' ? (
                      <span className="ml-2 text-[12px] text-[#9ca3af]">ne gösterdiği yazılmamış</span>
                    ) : (
                      <span className="ml-2 text-[12px] text-[#374151]">→ {v.label}</span>
                    )}
                  </dd>
                ))}
              </dl>

              {/*
                §109.5 — KURULUM TALİMATI. Bu blok olmadan split-URL ölü yüzeydir:
                operatör URL'leri girer ama yönlendirmeyi nasıl kuracağını bilemez.
                Ürün yönlendirmez (§67); yapması gerekeni SÖYLER.
              */}
              {sorgu.data.experiment.variants.some((v) => v.url !== undefined) ? (
                <div className="mt-4 rounded-lg border border-[#e5e7eb] bg-[#f9fafb] p-3" data-testid="deney-split-kurulum">
                  <p className="text-[12px] font-semibold text-[#374151]">
                    Split-URL kurulumu — bu adımı SUNUCUNDA yapman gerekiyor
                  </p>
                  <p className="mt-1 text-[12px] text-[#6b7280]">
                    Ziyaretçiyi kendi koluna yönlendir ve kararı adrese yaz. SDK atamayı
                    yeniden hesaplamaz, senin kararını devralır — ölçüm ile ziyaretçinin
                    gördüğü sayfa böylece AYNI kalır.
                  </p>
                  <ul className="mt-2 space-y-1">
                    {sorgu.data.experiment.variants
                      .filter((v) => v.url !== undefined)
                      .map((v) => (
                        <li key={v.key} className="num text-[12px] text-[#111827]">
                          {v.key} → <span className="font-medium">{v.url}</span>
                          <span className="text-[#6b7280]">
                            ?_dxp_v={sorgu.data.experiment.id}:{v.key}
                          </span>
                        </li>
                      ))}
                  </ul>
                  <p className="mt-2 text-[12px] text-[#9ca3af]">
                    Ağırlıkları sunucunda uygularsın (yukarıdaki yüzdeler). Varyant
                    sayfaları <code>rel=canonical</code> ile kontrol sayfasını göstermeli;{' '}
                    <code>noindex</code> kullanma (§109.2 KARAR 6).
                  </p>
                </div>
              ) : null}

              <div className="mt-4">
                <DeneyUygulamaDurumu
                  experiment={sorgu.data.experiment}
                  atanmisOturum={sorgu.data.results.reduce((acc, r) => acc + r.sessions, 0)}
                />
              </div>
            </Card>

            <Card title="Sonuçlar" subtitle="Sayılar motorun yazdığı kayıttan aynen okunur.">
              <div className="space-y-2" data-testid="deney-detay-sonuc">
                <DurumMetni experiment={sorgu.data.experiment} results={sorgu.data.results} />
                <ResultsTable experiment={sorgu.data.experiment} results={sorgu.data.results} />
              </div>
            </Card>
          </>
        ) : null}
      </div>
    </div>
  );
}
